import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import { 
  ArrowLeft, Save, FileText, User, Calendar, DollarSign, 
  Settings, Loader2, Search, Building, Phone, Mail,
  Plus, Trash2, Truck, Handshake, Building2, UserCircle,
  CalendarDays, Sun, Moon
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../../../hooks/use-toast';
import { useBillingPolicy } from '../../../hooks/useBillingPolicy';
import { cn } from '../../../lib/utils';
import { VEHICLE_TYPES } from '../../../constants/vehicleTypes';
import { DEFAULT_MATRIX } from '../../../constants/chargePostes';
import ChargeMatrixEditor from '../../../components/contracts/ChargeMatrixEditor';
import { Badge } from '../../../components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PARTNER_TYPE_LABELS = {
  'entreprise': 'Entreprise',
  'Entreprise ou societe de transport': 'Entreprise',
  'independant': 'Indépendant',
  'Personne morale': 'Indépendant',
  'chauffeur_proprietaire': 'Chauffeur-proprio',
  'Personne physique': 'Chauffeur-proprio',
};

const PARTNER_TYPE_ICONS = {
  'entreprise': Building2,
  'Entreprise ou societe de transport': Building2,
  'independant': Truck,
  'Personne morale': Truck,
  'chauffeur_proprietaire': UserCircle,
  'Personne physique': UserCircle,
};

const ContractCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  
  // Client search
  const [clientSearch, setClientSearch] = useState('');
  const [clients, setClients] = useState([]);
  const [searchingClients, setSearchingClients] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  
  // Fleet items (for fleet_location billing mode)
  const [fleetItems, setFleetItems] = useState([
    { vehicleType: '', vehicleTypeLabel: '', vehicleTypeIcon: '🚛', quantity: 1, monthlyRatePerTruck: 0 }
  ]);

  // Matrice de prise en charge des charges (PM Spec 21/06/2026)
  const [chargeMatrix, setChargeMatrix] = useState(DEFAULT_MATRIX);
  
  // Truck slots (planning par poste - for fleet_location & monthly_fixed)
  const [truckSlots, setTruckSlots] = useState([]);
  
  // Partner selection
  const [partnerSearch, setPartnerSearch] = useState('');
  const [partnerResults, setPartnerResults] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [searchingPartners, setSearchingPartners] = useState(false);
  
  // Form data
  const billingPolicy = useBillingPolicy();
  const vatBlocked = !!billingPolicy?.vatBlocked;
  const [formData, setFormData] = useState({
    // Client
    clientId: '',
    clientName: '',
    clientContact: {
      name: '',
      phone: '',
      email: ''
    },
    
    // Période
    startDate: '',
    endDate: '',
    autoRenew: false,
    
    // Terminologie
    terminology: 'transfert',
    
    // Facturation
    billingMode: 'per_unit',
    monthlyFixedPrice: '',
    monthlyFixedDescription: '',
    pricePerUnit: '',
    pricePerTon: '',
    applyTVA: false,
    tauxTVA: 18,
    fuelPolicy: 'included',
    
    // Génération automatique de factures
    autoGenerateInvoice: false,
    autoGenerateDay: 1,
    sendInvoiceByEmail: true,
    
    // Options
    showDetailedLines: false,
    
    // Notes
    notes: ''
  });

  // Search clients
  const searchClients = async (query) => {
    setSearchingClients(true);
    try {
      // Rechercher dans les utilisateurs avec rôle client ou expediteur
      const url = query && query.length >= 2 
        ? `${API}/admin/users?search=${encodeURIComponent(query)}&pageSize=20`
        : `${API}/admin/users?pageSize=20`; // Charger les clients récents si pas de recherche
      
      const response = await axios.get(url);
      // La réponse utilise 'users' comme clé
      const allUsers = response.data.users || response.data.items || response.data || [];
      // Filtrer pour ne garder que les clients/expéditeurs
      const clientUsers = allUsers.filter(u => u.role === 'client' || u.role === 'expediteur' || u.accountType);
      setClients(clientUsers);
    } catch (error) {
      console.error('Error searching clients:', error);
    } finally {
      setSearchingClients(false);
    }
  };

  // Charger les clients au démarrage
  useEffect(() => {
    searchClients('');
    searchPartners('');
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchClients(clientSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [clientSearch]);

  // Search partners
  const searchPartners = async (query) => {
    setSearchingPartners(true);
    try {
      const headers = { 'Authorization': 'Bearer cookie-auth' };
      const params = new URLSearchParams({ limit: '20' });
      if (query) params.append('search', query);
      const res = await axios.get(`${API}/transport-partners/search-for-assignment?${params}`, { headers });
      setPartnerResults(res.data.partners || []);
    } catch (e) {
      console.error('Error searching partners:', e);
    } finally {
      setSearchingPartners(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => searchPartners(partnerSearch), 300);
    return () => clearTimeout(timer);
  }, [partnerSearch]);

  const selectPartner = (partner) => {
    setSelectedPartner(partner);
    toast({ title: "Partenaire sélectionné", description: `${partner.name} (${PARTNER_TYPE_LABELS[partner.type] || partner.type})` });
  };

  const removePartner = () => {
    setSelectedPartner(null);
  };

  const selectClient = (client) => {
    setSelectedClient(client);
    setFormData(prev => ({
      ...prev,
      clientId: client.id,
      clientName: client.companyName || `${client.firstName} ${client.lastName}`,
      clientContact: {
        name: `${client.firstName || ''} ${client.lastName || ''}`.trim(),
        phone: client.phone || '',
        email: client.email || ''
      }
    }));
    setClientSearch('');
    setClients([]);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleContactChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      clientContact: {
        ...prev.clientContact,
        [field]: value
      }
    }));
  };

  const validateStep1 = () => {
    if (!formData.clientId) {
      toast({
        title: "Erreur",
        description: "Veuillez sélectionner un client",
        variant: "destructive"
      });
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.startDate || !formData.endDate) {
      toast({
        title: "Erreur",
        description: "Veuillez renseigner les dates de début et fin",
        variant: "destructive"
      });
      return false;
    }
    
    if (new Date(formData.endDate) <= new Date(formData.startDate)) {
      toast({
        title: "Erreur",
        description: "La date de fin doit être postérieure à la date de début",
        variant: "destructive"
      });
      return false;
    }
    
    return true;
  };

  // ── Fleet helpers ─────────────────────────────────────────
  const fleetTotal = fleetItems.reduce(
    (sum, item) => sum + (item.quantity || 0) * (item.monthlyRatePerTruck || 0), 0
  );
  const fleetTotalTrucks = fleetItems.reduce((sum, i) => sum + (i.quantity || 0), 0);

  const addFleetItem = () => {
    setFleetItems(prev => [
      ...prev,
      { vehicleType: '', vehicleTypeLabel: '', vehicleTypeIcon: '🚛', quantity: 1, monthlyRatePerTruck: 0 }
    ]);
  };

  const removeFleetItem = (index) => {
    setFleetItems(prev => prev.filter((_, i) => i !== index));
  };

  const updateFleetItem = (index, field, value) => {
    setFleetItems(prev => prev.map((item, i) => {
      if (i !== index) return item;
      if (field === 'vehicleType') {
        const found = VEHICLE_TYPES.find(v => v.value === value);
        return {
          ...item,
          vehicleType: value,
          vehicleTypeLabel: found ? found.label : value,
          vehicleTypeIcon: found ? (found.icon || '🚛') : '🚛'
        };
      }
      return { ...item, [field]: value };
    }));
  };

  // ── Truck Slots helpers ──────────────────────────────────
  const isSlotMode = formData.billingMode === 'fleet_location' || formData.billingMode === 'monthly_fixed';

  const generateSlotsFromFleet = () => {
    const newSlots = [];
    fleetItems.forEach((fi) => {
      const qty = fi.quantity || 0;
      const rate = fi.monthlyRatePerTruck || 0;
      const label = fi.vehicleTypeLabel || fi.vehicleType || 'Camion';
      for (let i = 0; i < qty; i++) {
        newSlots.push({
          slotId: `slot-${Date.now()}-${newSlots.length}`,
          label: `${label} #${i + 1}`,
          vehicleType: fi.vehicleType || '',
          monthlyRate: rate,
          daysOfWeek: ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'],
          shift: 'day',
          expectedRunsPerDay: 1,
        });
      }
    });
    return newSlots;
  };

  // Auto-generate slots when fleet changes in fleet_location mode
  useEffect(() => {
    if (formData.billingMode === 'fleet_location' && fleetItems.some(fi => fi.vehicleType)) {
      setTruckSlots(generateSlotsFromFleet());
    } else if (formData.billingMode === 'monthly_fixed' && truckSlots.length === 0) {
      setTruckSlots([{
        slotId: `slot-${Date.now()}`,
        label: 'Poste 1',
        vehicleType: '',
        monthlyRate: 0,
        daysOfWeek: ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'],
        shift: 'day',
        expectedRunsPerDay: 1,
      }]);
    } else if (formData.billingMode === 'per_unit' || formData.billingMode === 'per_tonnage') {
      setTruckSlots([]);
    }
  }, [fleetItems, formData.billingMode]);

  const updateSlot = (idx, field, value) => {
    setTruckSlots(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const toggleSlotDay = (idx, day) => {
    setTruckSlots(prev => prev.map((s, i) => {
      if (i !== idx) return s;
      const days = s.daysOfWeek.includes(day)
        ? s.daysOfWeek.filter(d => d !== day)
        : [...s.daysOfWeek, day];
      return { ...s, daysOfWeek: days };
    }));
  };

  const addSlot = () => {
    setTruckSlots(prev => [...prev, {
      slotId: `slot-${Date.now()}`,
      label: `Poste ${prev.length + 1}`,
      vehicleType: prev[0]?.vehicleType || '',
      monthlyRate: prev[0]?.monthlyRate || 0,
      daysOfWeek: ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'],
      shift: 'day',
      expectedRunsPerDay: 1,
    }]);
  };

  const removeSlot = (idx) => {
    setTruckSlots(prev => prev.filter((_, i) => i !== idx));
  };

  const SLOT_DAYS = [
    { key: 'lundi', short: 'L' },
    { key: 'mardi', short: 'M' },
    { key: 'mercredi', short: 'Me' },
    { key: 'jeudi', short: 'J' },
    { key: 'vendredi', short: 'V' },
    { key: 'samedi', short: 'S' },
    { key: 'dimanche', short: 'D' },
  ];

  // ──────────────────────────────────────────────────────────

  const validateStep3 = () => {
    if (formData.billingMode === 'monthly_fixed' && !formData.monthlyFixedPrice) {
      toast({
        title: "Erreur",
        description: "Veuillez renseigner le tarif mensuel forfaitaire",
        variant: "destructive"
      });
      return false;
    }
    
    if (formData.billingMode === 'per_unit' && !formData.pricePerUnit) {
      toast({
        title: "Erreur", 
        description: "Veuillez renseigner le tarif unitaire",
        variant: "destructive"
      });
      return false;
    }

    if (formData.billingMode === 'per_tonnage' && !formData.pricePerTon) {
      toast({
        title: "Erreur",
        description: "Veuillez renseigner le tarif par tonne",
        variant: "destructive"
      });
      return false;
    }

    if (formData.billingMode === 'fleet_location') {
      const invalid = fleetItems.some(i => !i.vehicleType || !i.quantity || !i.monthlyRatePerTruck);
      if (fleetItems.length === 0 || invalid) {
        toast({
          title: "Erreur",
          description: "Veuillez renseigner au moins un type de camion avec quantité et tarif",
          variant: "destructive"
        });
        return false;
      }
    }
    
    return true;
  };

  const nextStep = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    if (step === 3 && !validateStep3()) return;
    
    setStep(prev => Math.min(4, prev + 1));
  };

  const prevStep = () => {
    setStep(prev => Math.max(1, prev - 1));
  };

  const handleSubmit = async () => {
    if (!validateStep1() || !validateStep2() || !validateStep3()) return;
    
    setLoading(true);
    
    try {
      const payload = {
        clientId: formData.clientId,
        clientName: formData.clientName,
        clientContact: formData.clientContact.name ? formData.clientContact : null,
        startDate: formData.startDate,
        endDate: formData.endDate,
        autoRenew: formData.autoRenew,
        terminology: formData.terminology,
        billingRules: {
          billingMode: formData.billingMode,
          monthlyFixedPrice: formData.billingMode === 'monthly_fixed' ? parseFloat(formData.monthlyFixedPrice) : null,
          monthlyFixedDescription: formData.billingMode === 'monthly_fixed' ? formData.monthlyFixedDescription : null,
          pricePerUnit: formData.billingMode === 'per_unit' ? parseFloat(formData.pricePerUnit) : null,
          pricePerTon: formData.billingMode === 'per_tonnage' ? parseFloat(formData.pricePerTon) : null,
          // Fleet location
          fleetItems: formData.billingMode === 'fleet_location' ? fleetItems.map(i => ({
            ...i,
            quantity: parseInt(i.quantity) || 1,
            monthlyRatePerTruck: parseFloat(i.monthlyRatePerTruck) || 0
          })) : [],
          totalMonthlyFleetAmount: formData.billingMode === 'fleet_location' ? fleetTotal : null,
          applyTVA: formData.applyTVA,
          tauxTVA: parseFloat(formData.tauxTVA),
          fuelPolicy: formData.fuelPolicy,
          fuelAdvanceBudgetPerDay: null,
          autoGenerateInvoice: formData.autoGenerateInvoice,
          autoGenerateDay: formData.autoGenerateInvoice ? parseInt(formData.autoGenerateDay) : null,
          sendInvoiceByEmail: formData.sendInvoiceByEmail
        },
        invoiceDisplayOptions: {
          showDetailedLines: formData.showDetailedLines
        },
        transportPartnerId: selectedPartner?.id || null,
        transportPartnerName: selectedPartner?.name || null,
        notes: formData.notes || null,
        truckSlots: isSlotMode && truckSlots.length > 0 ? truckSlots : [],
        chargeMatrix: chargeMatrix,
      };
      
      const response = await axios.post(`${API}/contracts`, payload);
      
      toast({
        title: "Succès",
        description: `Contrat ${response.data.contract.contractNumber} créé avec succès`
      });
      
      navigate(`/admin/contracts/${response.data.contract.id}`);
      
    } catch (error) {
      console.error('Error creating contract:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible de créer le contrat",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3, 4].map((s, index) => (
        <React.Fragment key={s}>
          <div 
            className={cn(
              "flex items-center justify-center w-10 h-10 rounded-full font-medium transition-all",
              step >= s 
                ? "bg-red-600 text-white" 
                : "bg-gray-200 text-gray-500"
            )}
          >
            {s}
          </div>
          {index < 3 && (
            <div className={cn(
              "w-16 h-1 mx-2 transition-all",
              step > s ? "bg-red-600" : "bg-gray-200"
            )} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="w-5 h-5 text-red-600" />
          Sélection du Client
        </CardTitle>
        <CardDescription>
          Recherchez et sélectionnez le client pour ce contrat
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Client Search */}
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Rechercher un client par nom, email ou téléphone..."
              value={clientSearch}
              onChange={(e) => setClientSearch(e.target.value)}
              className="pl-10"
              data-testid="client-search-input"
            />
            {searchingClients && (
              <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />
            )}
          </div>
          
          {/* Search Results */}
          {clients.length > 0 && (
            <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
              {clients.map((client) => (
                <div
                  key={client.id}
                  className="p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => selectClient(client)}
                  data-testid={`select-client-${client.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-gray-500" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">
                        {client.companyName || `${client.firstName} ${client.lastName}`}
                      </p>
                      <p className="text-sm text-gray-500">{client.email}</p>
                    </div>
                    {client.accountType === 'entreprise' && (
                      <Building className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Selected Client */}
        {selectedClient && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm font-medium text-green-800 mb-2">Client sélectionné</p>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">{formData.clientName}</p>
                <p className="text-sm text-gray-500">{selectedClient.email}</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Contact Details */}
        {selectedClient && (
          <div className="space-y-4 pt-4 border-t">
            <p className="text-sm font-medium text-gray-700">Contact du contrat</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Nom du contact</Label>
                <Input
                  value={formData.clientContact.name}
                  onChange={(e) => handleContactChange('name', e.target.value)}
                  placeholder="Nom du contact"
                />
              </div>
              <div>
                <Label>Téléphone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    value={formData.clientContact.phone}
                    onChange={(e) => handleContactChange('phone', e.target.value)}
                    placeholder="+225 XX XX XX XX"
                    className="pl-10"
                  />
                </div>
              </div>
              <div>
                <Label>Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    type="email"
                    value={formData.clientContact.email}
                    onChange={(e) => handleContactChange('email', e.target.value)}
                    placeholder="email@exemple.ci"
                    className="pl-10"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Transport Partner Selection (optional) */}
        {selectedClient && (
          <div className="space-y-4 pt-4 border-t">
            <div className="flex items-center gap-2">
              <Handshake className="w-4 h-4 text-amber-600" />
              <p className="text-sm font-medium text-gray-700">Partenaire Transport</p>
              <Badge variant="outline" className="text-xs">Optionnel</Badge>
            </div>
            
            {selectedPartner ? (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="contract-selected-partner">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {(() => {
                      const Icon = PARTNER_TYPE_ICONS[selectedPartner.type] || Truck;
                      return <Icon className="w-5 h-5 text-amber-700" />;
                    })()}
                    <div>
                      <p className="font-medium text-amber-900">{selectedPartner.name}</p>
                      <p className="text-xs text-amber-700">
                        {PARTNER_TYPE_LABELS[selectedPartner.type] || selectedPartner.type}
                        {' — '}{selectedPartner.vehiclesCount || 0} vehicule(s), {selectedPartner.driversCount || 0} chauffeur(s)
                      </p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={removePartner} className="text-amber-700 hover:text-red-600" data-testid="remove-contract-partner">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Rechercher un partenaire transport..."
                    value={partnerSearch}
                    onChange={(e) => setPartnerSearch(e.target.value)}
                    className="pl-10"
                    data-testid="contract-partner-search"
                  />
                  {searchingPartners && <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />}
                </div>
                {partnerResults.length > 0 && (
                  <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                    {partnerResults.map((p) => {
                      const Icon = PARTNER_TYPE_ICONS[p.type] || Truck;
                      return (
                        <div key={p.id} className="p-3 hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => selectPartner(p)} data-testid={`contract-partner-${p.id}`}>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                              <Icon className="w-4 h-4 text-gray-600" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-sm">{p.name}</p>
                              <p className="text-xs text-gray-500">{PARTNER_TYPE_LABELS[p.type] || p.type} — {p.vehiclesCount || 0} vehicule(s)</p>
                            </div>
                            <Badge className={`text-xs ${p.availability === 'disponible' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'}`}>
                              {p.availability || 'inconnu'}
                            </Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderStep2 = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-red-600" />
          Période et Terminologie
        </CardTitle>
        <CardDescription>
          Définissez la durée du contrat et la terminologie utilisée
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Dates */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Date de début *</Label>
            <Input
              type="date"
              value={formData.startDate}
              onChange={(e) => handleInputChange('startDate', e.target.value)}
              data-testid="start-date-input"
            />
          </div>
          <div>
            <Label>Date de fin *</Label>
            <Input
              type="date"
              value={formData.endDate}
              onChange={(e) => handleInputChange('endDate', e.target.value)}
              data-testid="end-date-input"
            />
          </div>
        </div>
        
        {/* Auto Renew */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <p className="font-medium text-gray-900">Renouvellement automatique</p>
            <p className="text-sm text-gray-500">Le contrat sera reconduit automatiquement à échéance</p>
          </div>
          <Switch
            checked={formData.autoRenew}
            onCheckedChange={(checked) => handleInputChange('autoRenew', checked)}
          />
        </div>
        
        {/* Terminology */}
        <div>
          <Label>Terminologie des opérations</Label>
          <p className="text-sm text-gray-500 mb-2">
            Comment appeler les opérations dans ce contrat ?
          </p>
          <Select 
            value={formData.terminology} 
            onValueChange={(value) => handleInputChange('terminology', value)}
          >
            <SelectTrigger data-testid="terminology-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="transfert">Transfert(s)</SelectItem>
              <SelectItem value="operation">Opération(s)</SelectItem>
              <SelectItem value="course">Course(s)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  );

  const renderStep3 = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-red-600" />
          Règles de Facturation
        </CardTitle>
        <CardDescription>
          Configurez le mode de facturation du contrat
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Billing Mode */}
        <div>
          <Label>Mode de facturation *</Label>
          <Select 
            value={formData.billingMode} 
            onValueChange={(value) => handleInputChange('billingMode', value)}
          >
            <SelectTrigger data-testid="billing-mode-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly_fixed">Forfait mensuel fixe</SelectItem>
              <SelectItem value="per_unit">Facturation à l'unité</SelectItem>
              <SelectItem value="per_tonnage">Facturation au tonnage</SelectItem>
              <SelectItem value="fleet_location">Location de flotte (par camion)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Monthly Fixed Price */}
        {formData.billingMode === 'monthly_fixed' && (
          <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <Label>Prix mensuel forfaitaire (FCFA) *</Label>
              <Input
                type="number"
                value={formData.monthlyFixedPrice}
                onChange={(e) => handleInputChange('monthlyFixedPrice', e.target.value)}
                placeholder="6000000"
                data-testid="monthly-price-input"
              />
            </div>
            <div>
              <Label>Description du forfait</Label>
              <Input
                value={formData.monthlyFixedDescription}
                onChange={(e) => handleInputChange('monthlyFixedDescription', e.target.value)}
                placeholder="Ex: Mise à disposition mensuelle - 3 camions plateau"
              />
            </div>
          </div>
        )}
        
        {/* Per Unit Price */}
        {formData.billingMode === 'per_unit' && (
          <div className="p-4 bg-gray-50 rounded-lg">
            <Label>Prix par {formData.terminology === 'transfert' ? 'transfert' : formData.terminology === 'operation' ? 'opération' : 'course'} (FCFA) *</Label>
            <Input
              type="number"
              value={formData.pricePerUnit}
              onChange={(e) => handleInputChange('pricePerUnit', e.target.value)}
              placeholder="250000"
              data-testid="unit-price-input"
            />
          </div>
        )}

        {/* Per Tonnage Price */}
        {formData.billingMode === 'per_tonnage' && (
          <div className="p-4 bg-gray-50 rounded-lg">
            <Label>Prix par tonne (FCFA) *</Label>
            <Input
              type="number"
              value={formData.pricePerTon}
              onChange={(e) => handleInputChange('pricePerTon', e.target.value)}
              placeholder="5000"
              data-testid="tonnage-price-input"
            />
            <p className="text-xs text-gray-500 mt-1.5">Le tonnage est récupéré automatiquement depuis le poids net (pesée) de chaque transfert.</p>
          </div>
        )}

        {/* Fleet Location */}
        {formData.billingMode === 'fleet_location' && (
          <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-blue-900 flex items-center gap-2">
                  <Truck className="w-4 h-4" />
                  Composition de la flotte
                </p>
                <p className="text-xs text-blue-600 mt-0.5">
                  Le montant mensuel est calculé automatiquement : nb de camions × tarif/camion
                </p>
              </div>
              <Button type="button" size="sm" variant="outline" onClick={addFleetItem} className="border-blue-300 text-blue-700 hover:bg-blue-100">
                <Plus className="w-3 h-3 mr-1" /> Ajouter
              </Button>
            </div>

            {/* Header */}
            <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 px-1">
              <div className="col-span-5">Type de camion</div>
              <div className="col-span-2 text-center">Qté</div>
              <div className="col-span-4">Tarif/camion (FCFA)</div>
              <div className="col-span-1"></div>
            </div>

            {/* Fleet rows */}
            {fleetItems.map((item, index) => (
              <div key={index} className="grid grid-cols-12 gap-2 items-center bg-white rounded-lg p-2 border border-blue-100">
                <div className="col-span-5">
                  <Select value={item.vehicleType} onValueChange={(v) => updateFleetItem(index, 'vehicleType', v)}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="Choisir un type..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {VEHICLE_TYPES.filter(v => v.value && v.label && !v.category?.startsWith?.('group')).map(v => (
                        <SelectItem key={v.value} value={v.value}>
                          {v.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      min="1"
                      max="999"
                      value={item.quantity}
                      onChange={(e) => updateFleetItem(index, 'quantity', parseInt(e.target.value) || 1)}
                      className="h-8 text-sm text-center"
                      placeholder="ex: 3"
                    />
                    <span className="text-xs text-gray-400 whitespace-nowrap">cam.</span>
                  </div>
                </div>
                <div className="col-span-4">
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      min="0"
                      value={item.monthlyRatePerTruck}
                      onChange={(e) => updateFleetItem(index, 'monthlyRatePerTruck', parseFloat(e.target.value) || 0)}
                      className="h-8 text-sm"
                      placeholder="ex: 3000000"
                    />
                    <span className="text-xs text-gray-400 whitespace-nowrap">FCFA</span>
                  </div>
                </div>
                <div className="col-span-1 flex justify-center">
                  {fleetItems.length > 1 && (
                    <button type="button" onClick={() => removeFleetItem(index)} className="text-red-400 hover:text-red-600 p-1">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                {item.vehicleType && item.monthlyRatePerTruck > 0 && (
                  <div className="col-span-12 text-right text-xs text-blue-600 font-medium pr-1">
                    Sous-total : {new Intl.NumberFormat('fr-FR').format((item.quantity || 0) * (item.monthlyRatePerTruck || 0))} FCFA/mois
                  </div>
                )}
              </div>
            ))}

            {/* Total */}
            <div className="flex items-center justify-between pt-2 border-t border-blue-200">
              <div className="text-sm text-blue-700">
                <span className="font-medium">{fleetTotalTrucks} camion{fleetTotalTrucks > 1 ? 's' : ''}</span> mobilisé{fleetTotalTrucks > 1 ? 's' : ''}
              </div>
              <div className="text-right">
                <p className="text-xs text-blue-600">Redevance mensuelle totale</p>
                <p className="text-xl font-bold text-blue-900">
                  {new Intl.NumberFormat('fr-FR').format(fleetTotal)} <span className="text-sm font-normal">FCFA/mois</span>
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Truck Slots Planning (fleet_location & monthly_fixed) */}
        {isSlotMode && truckSlots.length > 0 && (
          <Card className="border-emerald-200 bg-emerald-50/30" data-testid="create-truck-slots">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm">
                <CalendarDays className="w-4 h-4 text-emerald-600" />
                Planning des postes
                <Badge variant="outline" className="text-xs ml-1">{truckSlots.length} poste{truckSlots.length > 1 ? 's' : ''}</Badge>
              </CardTitle>
              <CardDescription className="text-xs">
                Definissez les jours de travail, le shift et le nombre de transferts attendus par poste.
                {formData.billingMode === 'fleet_location' && ' Les postes sont generes automatiquement depuis la composition de flotte.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {truckSlots.map((slot, idx) => (
                <div key={slot.slotId} className="border border-gray-200 rounded-lg p-3 bg-white space-y-2" data-testid={`create-slot-${idx}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Input
                        value={slot.label}
                        onChange={e => updateSlot(idx, 'label', e.target.value)}
                        className="h-7 w-36 text-xs font-medium"
                      />
                      <span className="text-xs text-gray-400">{slot.vehicleType}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => updateSlot(idx, 'shift', slot.shift === 'day' ? 'night' : 'day')}
                        className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          slot.shift === 'day' ? 'bg-amber-100 text-amber-700' : 'bg-indigo-100 text-indigo-700'
                        }`}
                      >
                        {slot.shift === 'day' ? <Sun className="w-3 h-3" /> : <Moon className="w-3 h-3" />}
                        {slot.shift === 'day' ? 'Jour' : 'Nuit'}
                      </button>
                      {truckSlots.length > 1 && (
                        <button type="button" onClick={() => removeSlot(idx)} className="text-red-400 hover:text-red-600 p-0.5">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-0.5">
                      {SLOT_DAYS.map(d => (
                        <button
                          key={d.key}
                          type="button"
                          onClick={() => toggleSlotDay(idx, d.key)}
                          className={`w-7 h-7 rounded text-[10px] font-medium transition-all ${
                            slot.daysOfWeek.includes(d.key)
                              ? 'bg-emerald-600 text-white'
                              : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                          }`}
                        >
                          {d.short}
                        </button>
                      ))}
                    </div>
                    <div className="text-gray-200">|</div>
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-gray-500">Transferts/jour:</span>
                      <Input
                        type="number"
                        min={1}
                        max={10}
                        value={slot.expectedRunsPerDay}
                        onChange={e => updateSlot(idx, 'expectedRunsPerDay', Math.max(1, parseInt(e.target.value) || 1))}
                        className="h-7 w-14 text-center text-xs"
                      />
                    </div>
                  </div>
                </div>
              ))}
              <Button type="button" size="sm" variant="outline" onClick={addSlot} className="border-emerald-300 text-emerald-700 hover:bg-emerald-100 text-xs gap-1" data-testid="create-add-slot-btn">
                <Plus className="w-3 h-3" /> Ajouter un poste
              </Button>
            </CardContent>
          </Card>
        )}
        
        {/* TVA — grisée si le régime d'imposition interdit la TVA */}
        <div className={`flex items-center justify-between p-4 border rounded-lg ${vatBlocked ? 'bg-gray-100 border-gray-200' : ''}`} data-testid="contract-tva-block">
          <div>
            <p className={`font-medium ${vatBlocked ? 'text-gray-500' : 'text-gray-900'}`}>
              Appliquer la TVA
              {vatBlocked && <span className="ml-2 text-xs font-normal uppercase tracking-wide text-gray-500">indisponible</span>}
            </p>
            <p className="text-sm text-gray-500 max-w-xl">
              {vatBlocked ? billingPolicy.message : `TVA à ${formData.tauxTVA} % sur les factures`}
            </p>
          </div>
          <Switch
            checked={formData.applyTVA && !vatBlocked}
            disabled={vatBlocked}
            onCheckedChange={(checked) => handleInputChange('applyTVA', checked)}
            data-testid="contract-apply-tva-switch"
          />
        </div>
        
        {/* Fuel Policy */}
        <div>
          <Label>Politique carburant</Label>
          <Select 
            value={formData.fuelPolicy} 
            onValueChange={(value) => {
              handleInputChange('fuelPolicy', value);
              setChargeMatrix((m) => ({ ...m, fuel: value === 'excluded' ? 'client' : 'baobab' }));
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="included">Carburant inclus</SelectItem>
              <SelectItem value="excluded">Carburant exclu (à la charge du client)</SelectItem>
              <SelectItem value="advance_with_receipts">Avance avec justificatifs</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Matrice de prise en charge — PM Spec 21/06/2026 */}
        <div className="pt-4 border-t">
          <p className="font-medium text-gray-900">Qui paie quoi ?</p>
          <p className="text-sm text-gray-500 mb-3">
            Détermine la matrice de rentabilité : seuls les postes à notre charge sont comptés dans la marge.
          </p>
          <ChargeMatrixEditor
            value={chargeMatrix}
            onChange={setChargeMatrix}
            compact
          />
        </div>
      </CardContent>
    </Card>
  );

  const renderStep4 = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="w-5 h-5 text-red-600" />
          Options et Récapitulatif
        </CardTitle>
        <CardDescription>
          Options d'affichage et résumé du contrat
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Invoice Options */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div>
            <p className="font-medium text-gray-900">Détail des opérations sur facture</p>
            <p className="text-sm text-gray-500">
              Par défaut, une ligne groupée. Activez pour détailler chaque opération.
            </p>
          </div>
          <Switch
            checked={formData.showDetailedLines}
            onCheckedChange={(checked) => handleInputChange('showDetailedLines', checked)}
          />
        </div>
        
        {/* Auto Generate Invoice */}
        <div className="space-y-4 p-4 border rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Génération automatique de factures</p>
              <p className="text-sm text-gray-500">
                Génère automatiquement une facture chaque mois
              </p>
            </div>
            <Switch
              checked={formData.autoGenerateInvoice}
              onCheckedChange={(checked) => handleInputChange('autoGenerateInvoice', checked)}
            />
          </div>
          
          {formData.autoGenerateInvoice && (
            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
              <div>
                <Label>Jour de génération</Label>
                <Select 
                  value={formData.autoGenerateDay?.toString()} 
                  onValueChange={(value) => handleInputChange('autoGenerateDay', parseInt(value))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner le jour" />
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(28)].map((_, i) => (
                      <SelectItem key={i + 1} value={(i + 1).toString()}>
                        {i + 1}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  La facture sera générée le {formData.autoGenerateDay || 1} de chaque mois
                </p>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Envoyer par email</Label>
                  <p className="text-xs text-gray-500">Envoyer automatiquement la facture au client</p>
                </div>
                <Switch
                  checked={formData.sendInvoiceByEmail}
                  onCheckedChange={(checked) => handleInputChange('sendInvoiceByEmail', checked)}
                />
              </div>
            </div>
          )}
        </div>
        
        {/* Notes */}
        <div>
          <Label>Notes internes</Label>
          <Textarea
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            placeholder="Notes internes sur ce contrat..."
            rows={3}
          />
        </div>
        
        {/* Summary */}
        <div className="p-4 bg-gray-50 rounded-lg space-y-4">
          <p className="font-medium text-gray-900">Récapitulatif</p>
          
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Client</p>
              <p className="font-medium">{formData.clientName}</p>
            </div>
            <div>
              <p className="text-gray-500">Période</p>
              <p className="font-medium">
                {formData.startDate && formData.endDate 
                  ? `${new Date(formData.startDate).toLocaleDateString('fr-FR')} → ${new Date(formData.endDate).toLocaleDateString('fr-FR')}`
                  : '-'
                }
              </p>
            </div>
            <div>
              <p className="text-gray-500">Terminologie</p>
              <p className="font-medium capitalize">{formData.terminology === 'transfert' ? 'Transferts' : formData.terminology === 'operation' ? 'Opérations' : 'Courses'}</p>
            </div>
            <div>
              <p className="text-gray-500">Facturation</p>
              <p className="font-medium">
                {formData.billingMode === 'monthly_fixed'
                  ? `Forfait ${formData.monthlyFixedPrice ? new Intl.NumberFormat('fr-FR').format(formData.monthlyFixedPrice) + ' FCFA/mois' : '-'}`
                  : formData.billingMode === 'fleet_location'
                  ? `Location flotte — ${fleetTotalTrucks} camion${fleetTotalTrucks > 1 ? 's' : ''} — ${new Intl.NumberFormat('fr-FR').format(fleetTotal)} FCFA/mois`
                  : formData.billingMode === 'per_tonnage'
                  ? `Au tonnage ${formData.pricePerTon ? new Intl.NumberFormat('fr-FR').format(formData.pricePerTon) + ' FCFA/tonne' : '-'}`
                  : `À l'unité ${formData.pricePerUnit ? new Intl.NumberFormat('fr-FR').format(formData.pricePerUnit) + ' FCFA' : '-'}`
                }
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/admin/contracts')}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
        <div>
          <h1 className="text-lg md:text-2xl font-bold text-gray-900">Nouveau Contrat</h1>
          <p className="text-gray-500">Créer un contrat de location mensuelle</p>
        </div>
      </div>

      {/* Step Indicator */}
      {renderStepIndicator()}

      {/* Step Content */}
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
      {step === 4 && renderStep4()}

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={prevStep}
          disabled={step === 1}
        >
          Précédent
        </Button>
        
        {step < 4 ? (
          <Button
            onClick={nextStep}
            className="bg-red-600 hover:bg-red-700"
            data-testid="next-step-btn"
          >
            Suivant
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-red-600 hover:bg-red-700"
            data-testid="create-contract-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Création...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Créer le contrat
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ContractCreate;
